import React, { useState, useMemo } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import clsx from 'clsx';
import type { OrderVehiclesProps } from './OrderVehicles.types';
import type { UserVehicle, OrderAddonDto } from '@/api/types/timeline.types';
import { OrderVehicleCard } from './OrderVehicleCard';

const TRIGGER_BASE =
  'relative flex cursor-pointer items-center gap-2 justify-between px-5 transition-colors md:min-w-[270px] outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';

const LABEL_BASE = 'font-archivo font-medium tracking-[0.18px]';

const BADGE_BASE =
  'flex size-6 items-center justify-center rounded-[5px] font-inter font-semibold';

const TAB_CONTENT_CLASS =
  'rounded-[20px] md:rounded-tl-none mt-3 md:mt-0 bg-vehicleTabs-activeBg p-1';

const EMPTY_STATE_CLASS =
  'py-8 text-center font-inter text-sm text-grayText';

const CONCAVE_RIGHT = 'M0 0C0 16.5685 13.4315 30 30 30H0V0Z';
const CONCAVE_LEFT = 'M30 0C30 16.5685 16.5685 30 0 30H30V0Z';

const ConcaveCorner: React.FC<{ side: 'left' | 'right' }> = ({ side }) => (
  <svg
    className={clsx(
      'absolute bottom-0 z-0 hidden md:block',
      side === 'left' ? '-left-[30px]' : '-right-[30px]',
    )}
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d={side === 'left' ? CONCAVE_LEFT : CONCAVE_RIGHT}
      fill="#EFF4FF"
    />
  </svg>
);

const VehicleList: React.FC<{
  vehicles: UserVehicle[];
  emptyMessage: string;
  isArchived?: boolean;
  orderAddons?: OrderAddonDto[];
  isReportReady?: boolean;
}> = ({ vehicles, emptyMessage, isArchived, orderAddons, isReportReady }) => {
  if (vehicles.length === 0) {
    return <p className={EMPTY_STATE_CLASS}>{emptyMessage}</p>;
  }

  return (
    <div className="flex flex-col gap-1">
      {vehicles.map((vehicle) => (
        <OrderVehicleCard
          key={vehicle.id}
          vehicle={vehicle}
          isArchived={isArchived}
          orderAddons={orderAddons}
          isReportReady={isReportReady}
        />
      ))}
    </div>
  );
};

export const OrderVehicles: React.FC<OrderVehiclesProps> = ({
  vehicles,
  orderStatus,
  className,
}) => {
  const [activeTab, setActiveTab] = useState('current');

  const activeVehicles = useMemo(
    () => vehicles.filter((v) => !v.archived),
    [vehicles],
  );

  const archivedVehicles = useMemo(
    () => vehicles.filter((v) => v.archived),
    [vehicles],
  );

  if (vehicles.length === 0) return null;

  const isReportReady = orderStatus?.orderTimelineStatus === 'REPORT_READY';
  const isActive = (tab: string) => activeTab === tab;

  return (
    <div className={clsx('flex w-full justify-center bg-white', className)}>
      <section className="container flex flex-col gap-5 py-6">
        {/* Title with total count badge */}
        <div className="flex items-center gap-3">
          <h2 className="font-archivo text-[22px] font-semibold tracking-[0.22px] text-text-primary">
            Pojazdy w zamówieniu:
          </h2>
          <span className="flex size-7 items-center justify-center rounded-[5px] bg-text-primary font-inter text-lg font-semibold text-white">
            {vehicles.length}
          </span>
        </div>

        {/* Radix Tabs */}
        <Tabs.Root
          defaultValue="current"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <Tabs.List className="relative flex flex-col md:flex-row md:items-end" aria-label="Pojazdy w zamówieniu">
            {/* Current vehicle tab */}
            <Tabs.Trigger
              value="current"
              className={clsx(
                TRIGGER_BASE,
                'rounded-t-[20px]',
                isActive('current') ? 'py-3 md:py-6 z-20 bg-vehicleTabs-activeBg' : 'py-[13px] z-10 bg-gray-50 hover:bg-gray-100',
              )}
            >
              <div className="flex flex-col items-start gap-0.5">
                <span
                  className={clsx(
                    LABEL_BASE,
                    isActive('current')
                      ? 'text-[18px] text-text-primary'
                      : 'text-[15px] text-vehicleTabs-mutedText',
                  )}
                >
                  Aktualny pojazd
                </span>
                {isActive('current') && (
                  <span className="font-inter text-xs tracking-[0.2px] text-vehicleTabs-mutedText">
                    {orderStatus?.orderTimelineStatusDisplayName ||
                      'Zlecenie oczekuje na przyjęcie'}
                  </span>
                )}
              </div>
              <span
                className={clsx(
                  BADGE_BASE,
                  'text-base',
                  isActive('current')
                    ? 'bg-primary text-white md:mb-5'
                    : 'bg-vehicleTabs-badgeBg text-vehicleTabs-mutedBadgeText',
                )}
              >
                {activeVehicles.length}
              </span>

              {isActive('current') && <ConcaveCorner side="right" />}
            </Tabs.Trigger>

            {/* Archived vehicles tab */}
            <Tabs.Trigger
              value="archived"
              className={clsx(
                TRIGGER_BASE,
                'rounded-b-[20px] md:rounded-b-none md:rounded-t-[20px]',
                'z-10',
                isActive('archived') ? 'py-3 bg-vehicleTabs-activeBg' : 'py-[13px] bg-gray-50 hover:bg-gray-100',
              )}
            >
              {isActive('archived') && <ConcaveCorner side="left" />}

              <span
                className={clsx(
                  LABEL_BASE,
                  isActive('archived')
                    ? 'text-[18px] text-text-primary md:pb-5'
                    : 'text-[15px] text-vehicleTabs-mutedText',
                )}
              >
                Archiwalne pojazdy
              </span>
              <span
                className={clsx(
                  BADGE_BASE,
                  'text-sm',
                  isActive('archived')
                    ? 'bg-primary text-white md:mb-5'
                    : 'bg-vehicleTabs-badgeBg text-vehicleTabs-mutedBadgeText',
                )}
              >
                {archivedVehicles.length}
              </span>

              {isActive('archived') && <ConcaveCorner side="right" />}
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="current" className={TAB_CONTENT_CLASS}>
            <VehicleList
              vehicles={activeVehicles}
              emptyMessage="Brak aktywnych pojazdów w zamówieniu"
              orderAddons={orderStatus?.addons}
              isReportReady={isReportReady}
            />
          </Tabs.Content>

          <Tabs.Content value="archived" className={TAB_CONTENT_CLASS}>
            <VehicleList
              vehicles={archivedVehicles}
              emptyMessage="Brak archiwalnych pojazdów"
              isArchived
              orderAddons={orderStatus?.addons}
              isReportReady={isReportReady}
            />
          </Tabs.Content>
        </Tabs.Root>
      </section>
    </div>
  );
};
